'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import type { SearchParams } from './SearchInteractive';

interface ActiveFilters {
  maxPrice: number;
  stops: 'any' | '0' | '1' | '2+';
  airlines: string[];
  departTimeRange: 'any' | 'morning' | 'afternoon' | 'evening';
}

interface FlightFiltersProps {
  activeFilters: ActiveFilters;
  onFiltersChange: (filters: ActiveFilters) => void;
  searchParams: SearchParams;
}

const AIRLINE_OPTIONS = [
  'British Airways', 'Emirates', 'Lufthansa', 'Air France',
  'KLM', 'Turkish Airlines', 'Qatar Airways', 'Etihad Airways',
  'Ryanair', 'easyJet', 'Wizz Air', 'Virgin Atlantic',
];

const FlightFilters = ({ activeFilters, onFiltersChange, searchParams }: FlightFiltersProps) => {
  const [showAllAirlines, setShowAllAirlines] = useState(false);

  const updateFilter = <K extends keyof ActiveFilters>(key: K, value: ActiveFilters[K]) => {
    onFiltersChange({ ...activeFilters, [key]: value });
  };

  const toggleAirline = (airline: string) => {
    const current = activeFilters.airlines;
    if (current.includes(airline)) {
      updateFilter('airlines', current.filter(a => a !== airline));
    } else {
      updateFilter('airlines', [...current, airline]);
    }
  };

  const resetFilters = () => {
    onFiltersChange({
      maxPrice: 5000,
      stops: 'any',
      airlines: [],
      departTimeRange: 'any',
    });
  };

  const hasActiveFilters = activeFilters.maxPrice < 5000 || activeFilters.stops !== 'any' ||
    activeFilters.airlines.length > 0 || activeFilters.departTimeRange !== 'any';

  const visibleAirlines = showAllAirlines ? AIRLINE_OPTIONS : AIRLINE_OPTIONS.slice(0, 6);

  return (
    <div className="space-y-4">
      {/* Filter Header */}
      <div className="bg-card rounded-xl shadow-3d p-4 border-4 border-accent/20">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-black text-foreground flex items-center gap-2">
            <Icon name="FunnelIcon" size={18} className="text-accent" />
            Filters
          </h3>
          {hasActiveFilters && (
            <button onClick={resetFilters} className="text-xs text-primary font-bold hover:underline">
              Reset all
            </button>
          )}
        </div>

        {/* Price Range */}
        <div className="mb-5">
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-black text-foreground">Max Price</label>
            <span className="text-sm font-black text-primary">£{activeFilters.maxPrice.toLocaleString()}</span>
          </div>
          <input
            type="range"
            min={100}
            max={5000}
            step={50}
            value={activeFilters.maxPrice}
            onChange={e => updateFilter('maxPrice', parseInt(e.target.value))}
            className="w-full h-2 bg-border rounded-full appearance-none cursor-pointer accent-primary"
          />
          <div className="flex justify-between text-xs text-muted-foreground mt-1">
            <span>£100</span>
            <span>£5,000</span>
          </div>
        </div>

        {/* Stops */}
        <div className="mb-5">
          <label className="text-sm font-black text-foreground block mb-2">Stops</label>
          <div className="grid grid-cols-2 gap-2">
            {(['any', '0', '1', '2+'] as const).map(stop => (
              <button
                key={stop}
                onClick={() => updateFilter('stops', stop)}
                className={`py-2 px-3 rounded-lg text-xs font-black border-2 transition-all ${
                  activeFilters.stops === stop
                    ? 'bg-primary text-white border-primary' :'bg-surface text-foreground border-border hover:border-primary'
                }`}
              >
                {stop === 'any' ? 'Any' : stop === '0' ? '✈ Direct' : stop === '1' ? '1 Stop' : '2+ Stops'}
              </button>
            ))}
          </div>
        </div>

        {/* Departure Time */}
        <div className="mb-5">
          <label className="text-sm font-black text-foreground block mb-2">Departure Time</label>
          <div className="space-y-2">
            {([
              { value: 'any', label: 'Any time', icon: '🕐' },
              { value: 'morning', label: 'Morning (05:00–12:00)', icon: '🌅' },
              { value: 'afternoon', label: 'Afternoon (12:00–18:00)', icon: '☀️' },
              { value: 'evening', label: 'Evening (18:00+)', icon: '🌙' },
            ] as const).map(opt => (
              <button
                key={opt.value}
                onClick={() => updateFilter('departTimeRange', opt.value)}
                className={`w-full flex items-center gap-2 py-2 px-3 rounded-lg text-xs font-bold border-2 transition-all text-left ${
                  activeFilters.departTimeRange === opt.value
                    ? 'bg-primary/10 text-primary border-primary' :'bg-surface text-foreground border-border hover:border-primary'
                }`}
              >
                <span>{opt.icon}</span>
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Airlines */}
        <div>
          <label className="text-sm font-black text-foreground block mb-2">
            Airlines
            {activeFilters.airlines.length > 0 && (
              <span className="ml-2 text-xs bg-primary text-white px-2 py-0.5 rounded-full">
                {activeFilters.airlines.length}
              </span>
            )}
          </label>
          <div className="space-y-2">
            {visibleAirlines.map(airline => (
              <label key={airline} className="flex items-center gap-2 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={activeFilters.airlines.includes(airline)}
                  onChange={() => toggleAirline(airline)}
                  className="w-4 h-4 accent-primary rounded"
                />
                <span className="text-xs font-bold text-foreground group-hover:text-primary transition-colors">
                  {airline}
                </span>
              </label>
            ))}
          </div>
          {AIRLINE_OPTIONS.length > 6 && (
            <button
              onClick={() => setShowAllAirlines(!showAllAirlines)}
              className="mt-2 text-xs text-primary font-bold hover:underline flex items-center gap-1"
            >
              {showAllAirlines ? 'Show less' : `+${AIRLINE_OPTIONS.length - 6} more airlines`}
              <Icon name={showAllAirlines ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={12} />
            </button>
          )}
        </div>
      </div>

      {/* Book with Us CTA */}
      <div className="bg-gradient-to-br from-primary to-secondary rounded-xl p-4 text-center">
        <Icon name="PhoneIcon" size={24} className="text-white mx-auto mb-2" />
        <p className="text-sm font-black text-white mb-1">Need Help?</p>
        <p className="text-xs text-white/80 mb-3">Our experts find you the best deal</p>
        <a
          href="https://wa.me/447445270141"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 bg-white text-primary px-4 py-2 rounded-lg font-black text-xs hover:bg-white/90 transition-colors"
        >
          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
          WhatsApp Us
        </a>
      </div>

      {/* Skyscanner CTA */}
      <div className="bg-[#0770E3] rounded-xl p-4 text-center">
        <Icon name="ArrowTopRightOnSquareIcon" size={24} className="text-white mx-auto mb-2" />
        <p className="text-sm font-black text-white mb-1">More Options</p>
        <p className="text-xs text-white/80 mb-3">Compare 1000+ airlines on Skyscanner</p>
        <a
          href={`https://www.skyscanner.net/transport/flights/${searchParams.from.substring(0,3).toUpperCase()}/${searchParams.to.substring(0,3).toUpperCase()}/${searchParams.departDate.replace(/-/g,'').substring(2)}/?adults=${searchParams.passengers.adults}&cabinclass=${searchParams.cabinClass}&currency=GBP&locale=en-GB`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 bg-white text-[#0770E3] px-4 py-2 rounded-lg font-black text-xs hover:bg-white/90 transition-colors"
        >
          <Icon name="ArrowTopRightOnSquareIcon" size={12} />
          Open Skyscanner
        </a>
      </div>
    </div>
  );
};

export default FlightFilters;
